import React, { useEffect, useRef, useState, useCallback } from 'react';
import './App.css';

// Video URLs
const videoUrls = [
  '/videos/video1.mp4',
  '/videos/video2.mp4',
  '/videos/video3.mp4',
  '/videos/video4.mp4',
  '/videos/video5.mp4',
  '/videos/video6.mp4',
  '/videos/video7.mp4',
  '/videos/video8.mp4',
  '/videos/video9.mp4'
];

// Panel content
const panelContent = [
  { type: 'scroll-hint', lines: [] },
  { type: 'text', lines: ['Something is happening…', 'The legend is waking up.'], effect: 'glow-gold' },
  { type: 'text', lines: ['He is not alone.', 'A whole community is rising.'], effect: 'glow-warm' },
  { type: 'text', lines: ['This is not just a token.', 'This is a movement.'], effect: 'impact-gold' },
  { type: 'text', lines: ['The crew is ready.', 'The journey truly begins.'], effect: 'heroic' },
  { type: 'text', lines: ['Markets crash.', 'Fear rises.', 'But real pirates keep going.'], effect: 'storm' },
  { type: 'text', lines: ['No doubt.', 'No fear.', 'Only conviction.'], effect: 'determination' },
  { type: 'text', lines: ['The new world awaits…', 'Only the brave will enter.'], effect: 'revelation' },
  { type: 'final', lines: [] }
];

const frameVariants = [
  'frame-torn', 'frame-ink', 'frame-cracked', 'frame-splash',
  'frame-burst', 'frame-rough', 'frame-dynamic', 'frame-explosion', 'frame-epic'
];

const transitionTypes = ['slash', 'burst', 'zoom', 'wipe', 'spiral', 'thunder', 'flash', 'gate', 'explosion'];

// Loading/Splash Screen
const SplashScreen = ({ onEnter, loadingProgress }) => {
  return (
    <div className="splash-screen">
      <div className="splash-content">
        <div className="splash-logo">
          <span className="logo-kanji">海賊</span>
          <span className="logo-text">$LUFFY</span>
        </div>
        
        <div className="splash-loading">
          <div className="loading-bar">
            <div className="loading-fill" style={{ width: `${loadingProgress}%` }}></div>
          </div>
          <p className="loading-text">
            {loadingProgress < 100 ? `Loading... ${Math.round(loadingProgress)}%` : 'Ready!'}
          </p>
        </div>

        {loadingProgress >= 100 && (
          <button className="enter-btn" onClick={onEnter}>
            <span className="btn-kanji">入る</span>
            <span className="btn-text">ENTER THE ADVENTURE</span>
            <div className="btn-glow"></div>
          </button>
        )}

        <div className="splash-particles">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="particle" style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}></div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Video Panel for Mobile (full screen)
const VideoPanel = ({ videoUrl, index, isActive, content, videoRef }) => {
  const [showText, setShowText] = useState(false);
  const [showCTA, setShowCTA] = useState(false);
  const [copied, setCopied] = useState(false);
  const contractAddress = '9Vh33ee2iHam6WkyEKWPpnzRRy1BeJD8gA7YxV4qpump';

  useEffect(() => {
    if (isActive) {
      const video = videoRef?.current;
      if (video) {
        video.currentTime = 0;
        video.play().catch(() => {});
      }
      
      const textTimer = setTimeout(() => setShowText(true), 400);
      const ctaTimer = content.type === 'final' ? setTimeout(() => setShowCTA(true), 800) : null;
      
      return () => {
        clearTimeout(textTimer);
        if (ctaTimer) clearTimeout(ctaTimer);
      };
    } else {
      setShowText(false);
      setShowCTA(false);
    }
  }, [isActive, content.type, videoRef]);

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(contractAddress);
      setCopied(true);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = contractAddress;
      ta.style.cssText = 'position:fixed;left:-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
    }
    setTimeout(() => setCopied(false), 2000);
  }, []);

  return (
    <section className={`panel ${isActive ? 'active' : ''}`}>
      <div className={`manga-frame ${frameVariants[index]} ${isActive ? 'frame-active' : ''}`}>
        <div className="manga-inner">
          <video
            ref={videoRef}
            className="panel-video"
            src={videoUrl}
            muted
            playsInline
            preload="auto"
          />
        </div>
        <div className="corner corner-tl"></div>
        <div className="corner corner-tr"></div>
        <div className="corner corner-bl"></div>
        <div className="corner corner-br"></div>
        <div className="ink ink-1"></div>
        <div className="ink ink-2"></div>
        <div className="ink ink-3"></div>
        <div className="ink ink-4"></div>
      </div>

      {content.type === 'scroll-hint' && (
        <div className={`scroll-hint ${showText ? 'show' : ''}`}>
          <div className="scroll-arrows"><span></span><span></span><span></span></div>
          <p>スクロール</p>
        </div>
      )}

      {content.type === 'text' && (
        <div className={`text-overlay ${content.effect} ${showText ? 'show' : ''}`}>
          {content.lines.map((line, i) => (
            <p key={i} style={{ animationDelay: `${i * 0.1}s` }}>{line}</p>
          ))}
        </div>
      )}

      {content.type === 'final' && showCTA && (
        <div className="final-cta">
          <div className="cta-buttons">
            <a href="https://dexscreener.com/solana/5DF588M1FaEwnqPDPmmKPor1YpqR3j27QhmaAvqxRfQt" target="_blank" rel="noopener noreferrer" className="btn btn-trade">
              <span className="kanji">取引</span><span>Trade</span>
            </a>
            <a href="https://t.me/luffyonsolanaa" target="_blank" rel="noopener noreferrer" className="btn btn-tg">
              <span className="kanji">参加</span><span>Telegram</span>
            </a>
            <a href="https://x.com/i/communities/1997376124498690372" target="_blank" rel="noopener noreferrer" className="btn btn-x">
              <span className="kanji">仲間</span><span>Community</span>
            </a>
          </div>
          <div className="contract" onClick={copyToClipboard}>
            <span className="label">CA</span>
            <span className="address">{contractAddress}</span>
            <span className="copy">{copied ? '✓' : '⧉'}</span>
          </div>
        </div>
      )}
    </section>
  );
};

// Video Thumbnail for Desktop Grid (3x3)
const VideoThumbnail = ({ videoUrl, index, isActive, content, videoRef, onClick }) => {
  const [showText, setShowText] = useState(false);
  const [showCTA, setShowCTA] = useState(false);
  const [copied, setCopied] = useState(false);
  const contractAddress = '9Vh33ee2iHam6WkyEKWPpnzRRy1BeJD8gA7YxV4qpump';

  useEffect(() => {
    if (isActive) {
      const video = videoRef?.current;
      if (video) {
        video.currentTime = 0;
        video.play().catch(() => {});
      }
      
      const textTimer = setTimeout(() => setShowText(true), 400);
      const ctaTimer = content.type === 'final' ? setTimeout(() => setShowCTA(true), 800) : null;
      
      return () => {
        clearTimeout(textTimer);
        if (ctaTimer) clearTimeout(ctaTimer);
      };
    } else {
      setShowText(false);
      setShowCTA(false);
      const video = videoRef?.current;
      if (video) {
        video.pause();
      }
    }
  }, [isActive, content.type, videoRef]);

  const copyToClipboard = useCallback(async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(contractAddress);
      setCopied(true);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = contractAddress;
      ta.style.cssText = 'position:fixed;left:-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
    }
    setTimeout(() => setCopied(false), 2000);
  }, []);

  return (
    <div 
      className={`grid-thumbnail ${isActive ? 'active' : 'inactive'}`}
      onClick={onClick}
    >
      <div className={`manga-frame ${frameVariants[index]} ${isActive ? 'frame-active' : ''}`}>
        <div className="manga-inner">
          <video
            ref={videoRef}
            className="panel-video"
            src={videoUrl}
            muted
            playsInline
            preload="auto"
            loop={isActive}
          />
        </div>
        <div className="corner corner-tl"></div>
        <div className="corner corner-tr"></div>
        <div className="corner corner-bl"></div>
        <div className="corner corner-br"></div>
      </div>

      {/* Dark overlay for inactive thumbnails */}
      <div className="thumbnail-overlay"></div>

      {/* Panel number */}
      {false && <div className="thumbnail-number">{index + 1}</div>}

      {content.type === 'scroll-hint' && isActive && (
        <div className={`scroll-hint grid-hint ${showText ? 'show' : ''}`}>
          <div className="scroll-arrows"><span></span><span></span><span></span></div>
          <p>スクロール</p>
        </div>
      )}

      {content.type === 'text' && isActive && (
        <div className={`text-overlay grid-text ${content.effect} ${showText ? 'show' : ''}`}>
          {content.lines.map((line, i) => (
            <p key={i} style={{ animationDelay: `${i * 0.1}s` }}>{line}</p>
          ))}
        </div>
      )}

      {content.type === 'final' && isActive && showCTA && (
        <div className="final-cta grid-cta">
          <div className="cta-buttons">
            <a href="https://dexscreener.com/solana/5DF588M1FaEwnqPDPmmKPor1YpqR3j27QhmaAvqxRfQt" target="_blank" rel="noopener noreferrer" className="btn btn-trade" onClick={(e) => e.stopPropagation()}>
              <span className="kanji">取引</span><span>Trade</span>
            </a>
            <a href="https://t.me/luffyonsolanaa" target="_blank" rel="noopener noreferrer" className="btn btn-tg" onClick={(e) => e.stopPropagation()}>
              <span className="kanji">参加</span><span>Telegram</span>
            </a>
            <a href="https://x.com/i/communities/1997376124498690372" target="_blank" rel="noopener noreferrer" className="btn btn-x" onClick={(e) => e.stopPropagation()}>
              <span className="kanji">仲間</span><span>Community</span>
            </a>
          </div>
          <div className="contract" onClick={copyToClipboard}>
            <span className="label">CA</span>
            <span className="address">{contractAddress}</span>
            <span className="copy">{copied ? '✓' : '⧉'}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Progress
const Progress = ({ current, total }) => (
  <div className="progress">
    {[...Array(total)].map((_, i) => (
      <div key={i} className={`dot ${i === current ? 'active' : ''} ${i < current ? 'done' : ''}`} />
    ))}
  </div>
);

// Main App
function App() {
  const [started, setStarted] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [activePanel, setActivePanel] = useState(0);
  const [transition, setTransition] = useState('');
  const [isDesktop, setIsDesktop] = useState(false);
  const scrolling = useRef(false);
  const videoRefs = useRef(videoUrls.map(() => React.createRef()));

  // Check if desktop/tablet
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Preload videos and track progress
  useEffect(() => {
    let loaded = 0;
    const total = videoUrls.length;

    videoUrls.forEach((url, index) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      
      video.onloadeddata = () => {
        loaded++;
        setLoadingProgress((loaded / total) * 100);
      };
      
      video.onerror = () => {
        loaded++;
        setLoadingProgress((loaded / total) * 100);
      };
      
      video.src = url;
      video.load();
    });

    // Fallback - ensure we reach 100% after timeout
    const fallback = setTimeout(() => {
      setLoadingProgress(100);
    }, 8000);

    return () => clearTimeout(fallback);
  }, []);

  // Enter the experience
  const handleEnter = useCallback(() => {
    // Play all videos to unlock autoplay
    document.querySelectorAll('video').forEach(v => {
      v.muted = true;
      v.play().catch(() => {});
    });
    
    setStarted(true);
    
    // Start first video after splash fades
    setTimeout(() => {
      const firstVideo = videoRefs.current[0]?.current;
      if (firstVideo) {
        firstVideo.currentTime = 0;
        firstVideo.play().catch(() => {});
      }
    }, 500);
  }, []);

  // Handle scroll for mobile
  useEffect(() => {
    if (!started || isDesktop) return;

    const onScroll = () => {
      if (scrolling.current) return;
      
      const y = window.scrollY;
      const h = window.innerHeight;
      const newPanel = Math.round(y / h);
      
      if (newPanel !== activePanel && newPanel >= 0 && newPanel < 9) {
        scrolling.current = true;
        setTransition(transitionTypes[newPanel]);
        
        setTimeout(() => {
          setActivePanel(newPanel);
          setTimeout(() => {
            setTransition('');
            scrolling.current = false;
          }, 300);
        }, 150);
      }
    };
    
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [started, activePanel, isDesktop]);

  // Handle scroll for desktop grid
  useEffect(() => {
    if (!started || !isDesktop) return;

    const onWheel = (e) => {
      if (scrolling.current) return;
      
      const direction = e.deltaY > 0 ? 1 : -1;
      const newPanel = activePanel + direction;
      
      if (newPanel >= 0 && newPanel < 9) {
        scrolling.current = true;
        setTransition(transitionTypes[newPanel]);
        
        setTimeout(() => {
          setActivePanel(newPanel);
          setTimeout(() => {
            setTransition('');
            scrolling.current = false;
          }, 300);
        }, 150);
      }
    };
    
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [started, activePanel, isDesktop]);

  // Handle thumbnail click for desktop
  const handleThumbnailClick = useCallback((index) => {
    if (index !== activePanel) {
      setTransition(transitionTypes[index]);
      setTimeout(() => {
        setActivePanel(index);
        setTimeout(() => {
          setTransition('');
        }, 300);
      }, 150);
    }
  }, [activePanel]);

  // Show splash screen until user clicks enter
  if (!started) {
    return <SplashScreen onEnter={handleEnter} loadingProgress={loadingProgress} />;
  }

  // Desktop/Tablet: Grid layout with 3 per row, one row visible at a time
  if (isDesktop) {
    const activeRow = Math.floor(activePanel / 3);
    
    return (
      <div className="app app-desktop">
        <div className={`transition-layer ${transition}`}>
          <div className="trans-slash"></div>
          <div className="trans-burst"></div>
          <div className="trans-wipe"></div>
          <div className="trans-flash"></div>
        </div>

        <Progress current={activePanel} total={9} />

        <div className="grid-wrapper">
          <div className="grid-container" data-active-row={activeRow}>
            {videoUrls.map((url, i) => (
              <VideoThumbnail
                key={i}
                videoUrl={url}
                index={i}
                isActive={activePanel === i}
                content={panelContent[i]}
                videoRef={videoRefs.current[i]}
                onClick={() => handleThumbnailClick(i)}
              />
            ))}
          </div>
        </div>

        <div className="grid-scroll-hint">
          <span>Scroll to navigate</span>
          <div className="scroll-indicator">
            <span className="current">{activePanel + 1}</span>
            <span className="separator">/</span>
            <span className="total">9</span>
          </div>
        </div>
      </div>
    );
  }

  // Mobile: Vertical scroll layout
  return (
    <div className="app">
      <div className={`transition-layer ${transition}`}>
        <div className="trans-slash"></div>
        <div className="trans-burst"></div>
        <div className="trans-wipe"></div>
        <div className="trans-flash"></div>
      </div>

      <Progress current={activePanel} total={9} />

      {videoUrls.map((url, i) => (
        <VideoPanel
          key={i}
          videoUrl={url}
          index={i}
          isActive={activePanel === i}
          content={panelContent[i]}
          videoRef={videoRefs.current[i]}
        />
      ))}
    </div>
  );
}

export default App;
